import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { EmptyState, ErrorState, Field, Modal, Pagination, Spinner, StatusBadge } from '../../components/ui.jsx';
import { api, toQuery } from '../../api/client.js';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useMeta } from '../../state/MetaContext.jsx';
import { useToast } from '../../state/ToastContext.jsx';
import { timeAgo } from '../../lib/format.js';

const ResolveModal = ({ report, onClose, onDone }) => {
  const toast = useToast();
  const [note, setNote] = useState('');
  const [unpublish, setUnpublish] = useState(false);
  const [busy, setBusy] = useState(false);

  const decide = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/admin/reports/${report.id}`, {
        status,
        note: note.trim() || undefined,
        unpublishListing: status === 'resolved' ? unpublish : false
      });
      toast.success(status === 'resolved' ? 'Report resolved.' : 'Report dismissed.');
      onDone();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Close this report"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-outline" onClick={() => decide('dismissed')} disabled={busy}>
            Dismiss, nothing wrong
          </button>
          <button type="button" className="btn btn-primary" onClick={() => decide('resolved')} disabled={busy}>
            Resolve
          </button>
        </>
      }
    >
      <p className="modal-lead">
        Dismiss when the report does not hold up. Resolve when you have acted, and tick the box below if the listing
        should come down.
      </p>

      <Field label="What did you do" hint="Kept for the record, and sent to the landlord if you take the listing down.">
        <textarea rows="3" value={note} onChange={(event) => setNote(event.target.value)} />
      </Field>

      {report.propertyId ? (
        <label className="check">
          <input type="checkbox" checked={unpublish} onChange={(event) => setUnpublish(event.target.checked)} />
          <span>Take the listing down and tell the landlord why</span>
        </label>
      ) : null}
    </Modal>
  );
};

const AdminReports = () => {
  useTitle('Reports and complaints');
  const [params, setParams] = useSearchParams();
  const { reportReasons } = useMeta();
  const status = params.get('status') || 'open';
  const page = Number(params.get('page') || 1);

  const [resolving, setResolving] = useState(null);
  const { data, loading, error, reload } = useFetch(`/admin/reports${toQuery({ status, page, pageSize: 15 })}`);

  const reasonLabel = (slug) => reportReasons.find((item) => item.slug === slug)?.label || slug;

  const setParam = (patch) => setParams(new URLSearchParams(toQuery({ status, page: 1, ...patch }).replace(/^\?/, '')));

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>Reports and complaints</h1>
          <p className="muted">What tenants have flagged. Deal with the money-up-front reports first.</p>
        </div>
      </header>

      <div className="tab-row">
        {[
          ['open', 'Open'],
          ['resolved', 'Resolved'],
          ['dismissed', 'Dismissed'],
          ['any', 'All']
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={`tab ${status === value ? 'is-current' : ''}`}
            onClick={() => setParam({ status: value })}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? <Spinner /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}

      {data && data.items.length === 0 ? (
        <EmptyState
          title={status === 'open' ? 'Nothing outstanding' : 'Nothing here'}
          message={status === 'open' ? 'No open reports. Good sign.' : 'Try another tab.'}
        />
      ) : null}

      <div className="report-list">
        {(data?.items || []).map((report) => (
          <article key={report.id} className="report-card">
            <header>
              <div>
                <strong>{reasonLabel(report.reason)}</strong>
                <span className="muted">
                  {report.reporter ? `Reported by ${report.reporter.name}` : 'Reported anonymously'} -{' '}
                  {timeAgo(report.createdAt)}
                </span>
              </div>
              <StatusBadge status={report.status} />
            </header>

            {report.details ? <p className="report-details">{report.details}</p> : null}

            <p className="muted">
              {report.propertyId ? (
                <>
                  Listing: <Link to={`/properties/${report.propertyId}`}>{report.propertyTitle}</Link>
                  {report.propertyCity ? ` (${report.propertyCity})` : ''}
                </>
              ) : (
                'No listing attached'
              )}
            </p>

            {report.resolutionNote ? (
              <p className="decision-note">
                <strong>Outcome:</strong> {report.resolutionNote}
              </p>
            ) : null}

            <footer className="row-actions">
              {report.propertyId ? (
                <Link to={`/properties/${report.propertyId}`} className="btn btn-ghost btn-sm">
                  Inspect the listing
                </Link>
              ) : null}
              {report.status === 'open' ? (
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setResolving(report)}>
                  Close this report
                </button>
              ) : null}
            </footer>
          </article>
        ))}
      </div>

      {data ? <Pagination page={data.page} totalPages={data.totalPages} onChange={(next) => setParam({ page: next })} /> : null}

      {resolving ? (
        <ResolveModal
          report={resolving}
          onClose={() => setResolving(null)}
          onDone={() => {
            setResolving(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
};

export default AdminReports;
